/* ==========================================================================
   Third Hand High — theme.js
   Vanilla JS, no dependencies. Modules:
     1. Header navigation (mobile toggle, submenu close-on-outside-click)
     2. Cart status toast + header count
     3. Quantity steppers
     4. Product form (variant picker, gallery, AJAX add to cart)
     5. Size chart dialog
     6. Accordions (single-open FAQ)
     7. Cart page (line updates/removal via cart.js + Section Rendering API)
     8. Collection sort
     9. Customer address forms (country/province, toggles, delete)
    10. Login / recover panel toggle
   ========================================================================== */
(function () {
  'use strict';

  var strings = window.themeStrings || {};
  var routes = window.themeRoutes || {};

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $$(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function formatMoney(cents) {
    // Uses the shop money format when present, otherwise a plain fallback.
    var format = window.themeMoneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amount = parts.join('.');
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', parts[0])
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  function fetchJSON(url, options) {
    options = options || {};
    options.headers = Object.assign(
      { 'Content-Type': 'application/json', Accept: 'application/json' },
      options.headers || {}
    );
    if (options.body && typeof options.body !== 'string') {
      options.body = JSON.stringify(options.body);
    }
    return fetch(url, options).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || strings.cartError);
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  /* ------------------------------------------------------------------------
     1. Header navigation
     ------------------------------------------------------------------------ */
  function initHeader() {
    var toggle = $('[data-nav-toggle]');
    var nav = $('[data-nav]');
    if (!toggle || !nav) return;

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      nav.classList.toggle('is-open', open);
    }

    toggle.addEventListener('click', function () {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        setOpen(false);
        toggle.focus();
      }
    });

    // Close desktop submenus when clicking or tabbing away.
    var details = $$('[data-nav-details]');
    document.addEventListener('click', function (event) {
      details.forEach(function (item) {
        if (item.open && !item.contains(event.target)) item.open = false;
      });
    });
    details.forEach(function (item) {
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) item.open = false;
      });
      item.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && item.open) {
          item.open = false;
          $('summary', item).focus();
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     2. Cart status toast + header count
     ------------------------------------------------------------------------ */
  var statusTimer;

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
    $$('[data-cart-count-label]').forEach(function (el) {
      el.textContent = count === 1 ? '1 item in cart' : count + ' items in cart';
    });
  }

  function showStatus(message, isError) {
    var status = $('#CartStatus');
    if (!status) return;
    clearTimeout(statusTimer);
    $('.cart-status__text', status).textContent = message;
    status.classList.toggle('cart-status--error', !!isError);
    status.hidden = false;
    // Force a frame so the transition runs.
    requestAnimationFrame(function () {
      status.classList.add('is-visible');
    });
    statusTimer = setTimeout(function () {
      status.classList.remove('is-visible');
      setTimeout(function () {
        status.hidden = true;
      }, 260);
    }, isError ? 6000 : 4000);
  }

  /* ------------------------------------------------------------------------
     3. Quantity steppers
     ------------------------------------------------------------------------ */
  function initQuantitySteppers(root) {
    $$('.qty', root).forEach(function (qty) {
      if (qty.dataset.bound) return;
      qty.dataset.bound = 'true';
      var input = $('[data-qty-input]', qty);
      var minus = $('[data-qty-minus]', qty);
      var plus = $('[data-qty-plus]', qty);
      if (!input) return;
      var min = parseInt(input.min, 10);
      if (isNaN(min)) min = 0;

      function change(delta) {
        var value = parseInt(input.value, 10);
        if (isNaN(value)) value = min;
        value = Math.max(min, value + delta);
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (minus) minus.addEventListener('click', function () { change(-1); });
      if (plus) plus.addEventListener('click', function () { change(1); });
    });
  }

  /* ------------------------------------------------------------------------
     4. Product form
     ------------------------------------------------------------------------ */
  function initProduct(section) {
    var form = $('[data-product-form]', section);
    if (!form) return;
    var variantsScript = $('[data-variants]', form);
    var variants = [];
    try {
      variants = JSON.parse(variantsScript ? variantsScript.textContent : '[]');
    } catch (e) {
      variants = [];
    }

    var idInput = $('[data-variant-id]', form);
    var submit = $('[data-add-to-cart]', form);
    var submitText = $('[data-add-to-cart-text]', form);
    var errorEl = $('[data-form-error]', form);
    var priceEl = $('#ProductPrice', section);
    var fieldsets = $$('[data-option-index]', form);
    var stageItems = $$('[data-media-id]', $('[data-gallery]', section) || section).filter(function (el) {
      return el.classList.contains('product__stage-item');
    });
    var thumbs = $$('[data-thumb]', section);

    function selectedOptions() {
      return fieldsets.map(function (fieldset) {
        var checked = $('input:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    function findVariant(options) {
      return variants.filter(function (variant) {
        return options.every(function (value, index) {
          return variant.options[index] === value;
        });
      })[0] || null;
    }

    function showMedia(mediaId) {
      if (!mediaId) return;
      var matched = false;
      stageItems.forEach(function (item) {
        var active = String(item.dataset.mediaId) === String(mediaId);
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        if (active) matched = true;
      });
      if (!matched) return;
      thumbs.forEach(function (thumb) {
        var active = String(thumb.dataset.mediaId) === String(mediaId);
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    function markAvailability(options) {
      // Grey out values that have no available variant given the other selections.
      fieldsets.forEach(function (fieldset, index) {
        $$('[data-option-input]', fieldset).forEach(function (input) {
          var candidate = options.slice();
          candidate[index] = input.value;
          var exists = variants.some(function (variant) {
            return candidate.every(function (value, i) {
              return value === null || variant.options[i] === value;
            }) && variant.available;
          });
          var label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !exists);
        });
      });
    }

    function updatePrice(variant) {
      if (!priceEl || !variant) return;
      var current = $('[data-price-current]', priceEl);
      var compare = $('[data-price-compare]', priceEl);
      if (current) current.textContent = formatMoney(variant.price);
      var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compare) {
        compare.hidden = !onSale;
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
      }
      priceEl.classList.toggle('price--sale', !!onSale);
    }

    function updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    function render() {
      var options = selectedOptions();
      var variant = findVariant(options);

      fieldsets.forEach(function (fieldset, index) {
        var valueEl = $('[data-option-value="' + index + '"]', fieldset);
        if (valueEl) valueEl.textContent = options[index] || '';
      });

      markAvailability(options);

      if (!variant) {
        if (submit) submit.disabled = true;
        if (submitText) submitText.textContent = strings.unavailable || 'Unavailable';
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (submit) submit.disabled = !variant.available;
      if (submitText) {
        submitText.textContent = variant.available ? (strings.addToCart || 'Add to cart') : (strings.soldOut || 'Sold out');
      }
      updatePrice(variant);
      updateUrl(variant);
      if (variant.featured_media) showMedia(variant.featured_media.id);
    }

    fieldsets.forEach(function (fieldset) {
      fieldset.addEventListener('change', render);
    });

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        showMedia(thumb.dataset.mediaId);
      });
    });

    // Prev/next arrows, arrow keys and swipe step through the gallery; the active thumb scrolls into view.
    function stepMedia(dir) {
      if (stageItems.length < 2) return;
      var i = stageItems.findIndex(function (el) { return !el.hidden; });
      var next = stageItems[(i + dir + stageItems.length) % stageItems.length];
      showMedia(next.dataset.mediaId);
      var t = thumbs.filter(function (th) { return th.classList.contains('is-active'); })[0];
      if (t) t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    $$('[data-gallery-step]', section).forEach(function (btn) {
      btn.addEventListener('click', function () { stepMedia(Number(btn.dataset.galleryStep)); });
    });
    var stage = $('.product__stage', section);
    if (stage) {
      stage.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') stepMedia(1);
        if (e.key === 'ArrowLeft') stepMedia(-1);
      });
      var touchX = null;
      stage.addEventListener('touchstart', function (e) { touchX = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (touchX === null) return;
        var dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 40) stepMedia(dx < 0 ? 1 : -1);
        touchX = null;
      });
    }

    initQuantitySteppers(section);

    // AJAX add to cart
    form.addEventListener('submit', function (event) {
      if (!window.fetch) return; // let the browser post normally
      event.preventDefault();
      if (submit && submit.disabled) return;
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      var formData = new FormData(form);
      var body = {
        items: [
          {
            id: parseInt(formData.get('id'), 10),
            quantity: parseInt(formData.get('quantity'), 10) || 1
          }
        ]
      };
      var originalText = submitText ? submitText.textContent : '';
      if (submit) submit.setAttribute('aria-busy', 'true');

      fetchJSON(routes.cartAddUrl || '/cart/add.js', { method: 'POST', body: body })
        .then(function () {
          return fetchJSON(routes.cartJsonUrl || '/cart.js');
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          showStatus(strings.addedToCart || 'Added to cart.');
        })
        .catch(function (error) {
          var message = (error && error.message) || strings.cartError;
          if (errorEl) {
            errorEl.textContent = message;
            errorEl.hidden = false;
          } else {
            showStatus(message, true);
          }
        })
        .then(function () {
          if (submit) submit.removeAttribute('aria-busy');
          if (submitText) submitText.textContent = originalText;
        });
    });

    // Initial state so unavailable combos are marked on load.
    markAvailability(selectedOptions());
  }

  /* ------------------------------------------------------------------------
     5. Size chart dialog
     ------------------------------------------------------------------------ */
  function initSizeChart() {
    var dialog = $('[data-size-chart]');
    if (!dialog) return;
    var opener = null;

    function close() {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
      if (opener) opener.focus();
    }

    $$('[data-size-chart-open]').forEach(function (button) {
      button.addEventListener('click', function () {
        opener = button;
        if (typeof dialog.showModal === 'function') {
          dialog.showModal();
        } else {
          dialog.setAttribute('open', '');
        }
        var closeBtn = $('[data-size-chart-close]', dialog);
        if (closeBtn) closeBtn.focus();
      });
    });

    $$('[data-size-chart-close]', dialog).forEach(function (button) {
      button.addEventListener('click', close);
    });

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close(); // backdrop click
    });
  }

  /* ------------------------------------------------------------------------
     6. Accordions
     ------------------------------------------------------------------------ */
  function initAccordions() {
    $$('[data-accordion-single]').forEach(function (group) {
      var items = $$('[data-accordion-item]', group);
      items.forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) {
            if (other !== item && other.open) other.open = false;
          });
        });
      });
    });

    // Deep-link: open an accordion item when its id is in the hash.
    if (window.location.hash) {
      var target = document.getElementById(window.location.hash.slice(1));
      if (target && target.hasAttribute('data-accordion-item')) target.open = true;
    }
  }

  /* ------------------------------------------------------------------------
     7. Cart page
     ------------------------------------------------------------------------ */
  function initCart() {
    var section = $('[data-cart-section]');
    if (!section) return;
    var sectionId = section.dataset.sectionId;
    var headerSection = $('[data-header]');
    var headerSectionId = headerSection ? headerSection.closest('.shopify-section') : null;
    headerSectionId = headerSectionId ? headerSectionId.id.replace('shopify-section-', '') : null;
    var busy = false;

    function setError(message) {
      var errorEl = $('[data-cart-error]', section);
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    }

    function rerender(html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var fresh = $('[data-cart-section]', doc);
      if (fresh) {
        section.innerHTML = fresh.innerHTML;
        bind();
      }
    }

    function change(line, quantity) {
      if (busy) return;
      busy = true;
      section.classList.add('is-loading');
      setError('');
      var sections = [sectionId];
      if (headerSectionId) sections.push(headerSectionId);

      fetchJSON(routes.cartChangeUrl || '/cart/change.js', {
        method: 'POST',
        body: { line: line, quantity: quantity, sections: sections.join(','), sections_url: window.location.pathname }
      })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          if (cart.sections && cart.sections[sectionId]) {
            rerender(cart.sections[sectionId]);
          } else {
            window.location.reload();
          }
          if (cart.sections && headerSectionId && cart.sections[headerSectionId]) {
            var doc = new DOMParser().parseFromString(cart.sections[headerSectionId], 'text/html');
            var freshCount = $('[data-cart-count]', doc);
            if (freshCount) updateCartCount(parseInt(freshCount.textContent, 10) || 0);
          }
        })
        .catch(function (error) {
          setError((error && error.message) || strings.cartError);
          // Re-render from server truth so quantities are not lying.
          if (error && error.data && error.data.status === 422) {
            fetch(window.location.pathname + '?sections=' + sectionId)
              .then(function (r) { return r.json(); })
              .then(function (data) { if (data[sectionId]) rerender(data[sectionId]); })
              .catch(function () {});
          }
        })
        .then(function () {
          busy = false;
          section.classList.remove('is-loading');
        });
    }

    var noteTimer;
    function saveNote(value) {
      clearTimeout(noteTimer);
      noteTimer = setTimeout(function () {
        fetchJSON(routes.cartUpdateUrl || '/cart/update.js', { method: 'POST', body: { note: value } }).catch(function () {});
      }, 500);
    }

    function bind() {
      initQuantitySteppers(section);

      $$('[data-qty-input][data-line]', section).forEach(function (input) {
        var timer;
        input.addEventListener('change', function () {
          clearTimeout(timer);
          var qty = parseInt(input.value, 10);
          if (isNaN(qty) || qty < 0) qty = 0;
          timer = setTimeout(function () {
            change(parseInt(input.dataset.line, 10), qty);
          }, 150);
        });
      });

      $$('[data-cart-remove]', section).forEach(function (link) {
        link.addEventListener('click', function (event) {
          event.preventDefault();
          change(parseInt(link.dataset.line, 10), 0);
        });
      });

      var note = $('[data-cart-note]', section);
      if (note) {
        note.addEventListener('input', function () { saveNote(note.value); });
      }

      var form = $('[data-cart-form]', section);
      if (form) {
        form.addEventListener('submit', function (event) {
          // Only the checkout button submits; Enter in a quantity field should not.
          var submitter = event.submitter;
          if (submitter && submitter.name === 'checkout') return;
          if (!submitter) event.preventDefault();
        });
      }
    }

    bind();
  }

  /* ------------------------------------------------------------------------
     8. Collection sort
     ------------------------------------------------------------------------ */
  function initSort() {
    $$('[data-sort-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.assign(url.toString());
      });
    });
  }

  /* ------------------------------------------------------------------------
     9. Customer addresses
     ------------------------------------------------------------------------ */
  function initAddresses() {
    var root = $('[data-addresses]');
    if (!root) return;

    $$('[data-address-toggle]', root).forEach(function (button) {
      button.addEventListener('click', function () {
        var panel = document.getElementById(button.dataset.addressToggle);
        if (!panel) return;
        var open = panel.hidden;
        panel.hidden = !open;
        $$('[aria-controls="' + panel.id + '"]', root).forEach(function (control) {
          control.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        if (open) {
          var first = $('input, select', panel);
          if (first) first.focus();
        }
      });
    });

    $$('[data-address-delete]', root).forEach(function (button) {
      button.addEventListener('click', function () {
        if (!window.confirm(button.dataset.confirm || 'Delete this address?')) return;
        var form = document.createElement('form');
        form.method = 'post';
        form.action = button.dataset.addressDelete;
        var method = document.createElement('input');
        method.type = 'hidden';
        method.name = '_method';
        method.value = 'delete';
        form.appendChild(method);
        document.body.appendChild(form);
        form.submit();
      });
    });

    // Country / province selects (Shopify.CountryProvinceSelector is provided by
    // Shopify's shopify_common.js; we do a lightweight version ourselves).
    $$('[data-address-country]', root).forEach(function (country) {
      var province = country.closest('.form__row') ? $('[data-address-province]', country.closest('.form__row')) : null;
      if (!province) return;

      function fillProvinces() {
        var option = country.options[country.selectedIndex];
        var raw = option ? option.getAttribute('data-provinces') : null;
        var provinces = [];
        try {
          provinces = raw ? JSON.parse(raw) : [];
        } catch (e) {
          provinces = [];
        }
        province.innerHTML = '';
        var wrapper = province.closest('.form__field');
        if (!provinces.length) {
          if (wrapper) wrapper.hidden = true;
          return;
        }
        if (wrapper) wrapper.hidden = false;
        provinces.forEach(function (pair) {
          var opt = document.createElement('option');
          opt.value = pair[0];
          opt.textContent = pair[1];
          if (pair[0] === province.dataset.default) opt.selected = true;
          province.appendChild(opt);
        });
      }

      if (country.dataset.default) {
        Array.prototype.forEach.call(country.options, function (opt) {
          if (opt.value === country.dataset.default || opt.textContent === country.dataset.default) opt.selected = true;
        });
      }
      country.addEventListener('change', fillProvinces);
      fillProvinces();
    });
  }

  /* ------------------------------------------------------------------------
     10. Login / recover toggle
     ------------------------------------------------------------------------ */
  function initLogin() {
    var recover = $('[data-recover-panel]');
    if (!recover) return;
    var login = $('#LoginPanel');

    function show(panelToShow) {
      var showRecover = panelToShow === recover;
      recover.hidden = !showRecover;
      if (login) login.hidden = showRecover;
      var first = $('input', panelToShow);
      if (first) first.focus();
    }

    $$('[data-recover-open]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        show(recover);
      });
    });
    $$('[data-recover-close]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        show(login);
      });
    });

    if (window.location.hash === '#recover' || window.location.hash === '#RecoverPanel') show(recover);
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function init() {
    initHeader();
    $$('[data-product]').forEach(initProduct);
    initSizeChart();
    initAccordions();
    initCart();
    initSort();
    initAddresses();
    initLogin();
    initQuantitySteppers(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-init sections when they are re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if ($('[data-product]', section)) $$('[data-product]', section).forEach(initProduct);
    if ($('[data-cart-section]', section)) initCart();
    if ($('[data-nav-toggle]', section)) initHeader();
    initAccordions();
    initSizeChart();
    initQuantitySteppers(section);
  });
})();
